/**
 * 容量・送信数（05-ui.md §8.7）。
 *
 * 「上限に達すると新しいメールを受信できなくなる」という結果を必ず先に伝える。
 * 数字だけ出しても、契約者は何が起きるのか分からない。
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  Section,
  StatCard,
  UsageBar,
  formatBytes,
  formatCount,
} from '@mail-sv/ui';
import { HardDrive, Send } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { Async } from '../components/async.js';
import { Disclosure } from '../components/help.js';
import { formatLimit, usageRatio } from '../lib/format.js';
import { toUsageView } from '../lib/usage.js';
import type { AiPolicyView, UsageResponse } from '../lib/api-types.js';

/**
 * AI の当月使用量（`GET /ai/policy` の `usage`）。
 *
 * **「容量・送信数」の画面に置く理由。** 上限に達すると Webメールの
 * 要約・返信案が止まる（`onLimit='block'`）か、従量課金で加算される
 * （`onLimit='meter'`）。どちらも契約者にとっては「使った量で結果が変わる」
 * ものなので、容量や送信数と同じ場所で見られないと気付けない。
 * 設定そのものは `/ai` に置いてあるので、ここは**残量の表示と導線だけ**にする。
 *
 * 数字は `/ai/policy` から取る（`/ai/usage` は `usageRead` 権限が要る内訳で、
 * ここに要るのは合計と上限だけ）。プランに AI が無い組織・権限が無い
 * メンバーでは**何も出さない**（使えない機能のメーターを出しても混乱するだけ）。
 */
function AiTokenCard() {
  const policy = useQuery({
    queryKey: qk.aiPolicy(),
    queryFn: () => api.get<AiPolicyView>('/ai/policy'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (policy.isPending || policy.isError) return null;
  const data = policy.data;
  if (!data.plan.aiAssistant) return null;

  const used = data.usage.totalTokens;
  const cap = data.usage.cap;
  const ratio = usageRatio(used, cap);
  const enabled = data.effective.enabled;

  return (
    <Section
      title="AI の使用量（今月）"
      description="Webメールの要約・返信文の提案などで使われた量です。"
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {data.usage.month.slice(0, 4)} 年 {Number(data.usage.month.slice(4, 6))} 月
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!enabled ? (
            <Alert variant="info">
              <AlertTitle>AI 機能は現在オフです</AlertTitle>
              <AlertDescription>
                有効にすると、Webメールで要約や返信文の提案が使えるようになります。
              </AlertDescription>
            </Alert>
          ) : null}

          {data.usage.overCap ? (
            <Alert variant="warning">
              <AlertTitle>今月の上限に達しました</AlertTitle>
              <AlertDescription>
                上限に達したあとの動作（停止する / 従量課金で続ける）は AI
                機能の画面で選べます。
              </AlertDescription>
            </Alert>
          ) : null}

          <p className="text-3xl font-semibold tabular">
            {formatCount(used)}
            <span className="ml-2 text-base font-normal text-muted-foreground">
              / {cap > 0 ? `${formatCount(cap)} トークン` : '無制限'}
              {ratio !== null ? `（${Math.round(ratio * 100)}%）` : ''}
            </span>
          </p>
          <UsageBar used={used} total={cap} formatValue={(n) => formatCount(n)} />

          <p className="text-xs text-muted-foreground">
            内訳: 送った分 {formatCount(data.usage.inputTokens)} / 受け取った分{' '}
            {formatCount(data.usage.outputTokens)} ・ 呼び出し {formatCount(data.usage.calls)} 回
            {data.usage.failedCalls > 0 ? `（うち失敗 ${formatCount(data.usage.failedCalls)} 回）` : ''}
          </p>
          <p className="text-xs text-muted-foreground">
            トークンは AI が読み書きした量のおおよその単位です。日本語では 1 文字が 1〜2
            トークンほどになります。
          </p>

          <Button asChild variant="outline" size="sm">
            <AppLink to="/ai">AI 機能の設定と内訳を見る</AppLink>
          </Button>
        </CardContent>
      </Card>
    </Section>
  );
}

export function UsagePage() {
  const org = useOrg();
  const usage = useQuery({
    queryKey: qk.usage(),
    // API は `current` / `limits` / `perMailbox` の平たい形で返す。
    // 画面は「容量」と「送信数」の 2 束で扱うので境界で変換する。
    queryFn: async () => toUsageView(await api.get<UsageResponse>('/usage')),
    staleTime: 60_000,
  });

  return (
    <>
      <PageHeader
        title="容量・送信数"
        description="ご契約全体の使用状況と、メールアドレスごとの内訳です。"
      />

      <Async query={usage} lines={6}>
        {(data) => {
          const ratio = usageRatio(data.storage.usedBytes, data.storage.totalBytes);
          const sorted = [...data.storage.perMailbox].sort((a, b) => b.usedBytes - a.usedBytes);
          const maxUsed = sorted[0]?.usedBytes ?? 0;

          return (
            <div className="space-y-6">
              {ratio !== null && ratio >= 0.9 ? (
                <Alert variant={ratio >= 1 ? 'destructive' : 'warning'}>
                  <AlertTitle>
                    {ratio >= 1
                      ? '容量の上限に達しています'
                      : '容量がまもなく上限に達します'}
                  </AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      上限に達すると、新しいメールを受信できなくなります（送信者にはエラーが返ります）。
                      不要なメールを削除するか、プランを変更してください。
                    </p>
                    <Button asChild variant="outline" size="sm">
                      <AppLink to="/billing/plan">プランを変更する</AppLink>
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">ご契約全体の使用容量</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-3xl font-semibold tabular">
                    {formatBytes(data.storage.usedBytes)}
                    <span className="ml-2 text-base font-normal text-muted-foreground">
                      /{' '}
                      {data.storage.totalBytes > 0
                        ? formatBytes(data.storage.totalBytes)
                        : '無制限'}
                      {ratio !== null ? `（${Math.round(ratio * 100)}%）` : ''}
                    </span>
                  </p>
                  <UsageBar
                    used={data.storage.usedBytes}
                    total={data.storage.totalBytes}
                    formatValue={(n) => formatBytes(n)}
                  />
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatCard
                  label="本日の送信数"
                  value={formatCount(data.sending.sentToday)}
                  unit={`/ ${formatLimit(data.sending.perDayMax)} 通`}
                  icon={Send}
                  tone={
                    data.sending.perDayMax > 0 &&
                    data.sending.sentToday / data.sending.perDayMax >= 0.8
                      ? 'warning'
                      : 'default'
                  }
                />
                <StatCard
                  label="今月の送信数"
                  value={formatCount(data.sending.sentThisMonth)}
                  unit="通"
                  icon={Send}
                />
                <StatCard
                  label="メールアドレス数"
                  value={formatCount(org.data?.usage.mailboxes ?? 0)}
                  unit={`/ ${formatLimit(org.data?.plan.limits.mailboxesMax ?? 0)} 件`}
                  icon={HardDrive}
                />
              </div>

              <AiTokenCard />

              <Section
                title="メールアドレスごとの使用容量"
                description="使用量の多い順に表示しています。"
              >
                {sorted.length === 0 ? (
                  <p className="text-sm text-muted-foreground">まだメールアドレスがありません。</p>
                ) : (
                  <Card>
                    <CardContent className="divide-y divide-border p-0">
                      {sorted.map((m) => (
                        <div key={m.mailboxId} className="space-y-1.5 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <AppLink
                              to={`/mailboxes/${m.mailboxId}`}
                              className="truncate text-sm font-medium hover:underline"
                            >
                              {m.address}
                            </AppLink>
                            <span className="tabular text-xs text-muted-foreground">
                              {formatBytes(m.usedBytes)} /{' '}
                              {m.quotaBytes > 0 ? formatBytes(m.quotaBytes) : '無制限'}
                            </span>
                          </div>
                          {/* 相対比較のバー。上限ではなく「最も使っているアドレス」を基準にする方が、
                              どこを整理すべきかが一目で分かる。 */}
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{
                                width: `${maxUsed > 0 ? (m.usedBytes / maxUsed) * 100 : 0}%`,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </Section>

              <Disclosure summary="容量を空けるには">
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    サイズの大きい添付ファイル付きメールを探して削除します（Webメールの検索で
                    「添付あり」に絞り込めます）。
                  </li>
                  <li>「ゴミ箱」「迷惑メール」フォルダを空にします。削除しただけでは容量は戻りません。</li>
                  <li>
                    古いメールを端末にダウンロードして保存し、サーバーからは削除します
                    （メールソフトの「アーカイブ」機能）。
                  </li>
                  <li>それでも足りない場合は、メールアドレスごとの容量上限やプランを見直してください。</li>
                </ul>
              </Disclosure>
            </div>
          );
        }}
      </Async>
    </>
  );
}
